import { DateTime, SingleDayDateTime } from 'components/Date/DateUtil';
import { DetailsList } from 'components/DetailsList/DetailsList';
import { DetailsListItem } from 'components/DetailsList/Item/DetailsListItem';
import { DetailListSection } from 'components/DetailsList/Section/DetailListSection';
import { EXPECTED_TIMES } from 'utils/constants/general/expectedTimes';
import { EXPECTED_TIMES_ONCE } from 'utils/constants/general/expectedTimesOnce';
import { EXPECTED_TIMES_RECURRING } from 'utils/constants/general/expectedTimesRecurring';
import { TIMES_OF_DAY } from 'utils/constants/general/timesOfDay';
import { TIMES_OF_DAY_VOLOPS } from 'utils/constants/general/timesOfDayVolops';

export const formatAmountString = (amount: string) => {
  const parts = amount.split('.');
  const cents = parseInt(parts[1], 10);
  const dollars = parts[0];
  return `$${dollars}${cents > 0 ? `.${cents}` : ''}`;
};

type Props = {
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  startsLocal: string | null;
  endsLocal: string | null;
  feeAmount: string | null;
  feeRequired: boolean;
  isRecurring: boolean;
  timezone: string | null;
  welcomeGroups: boolean;
  welcomeFamilies: boolean;
  welcomeIntl: boolean;
  detailsStipendProvided: boolean;
  detailsTrainingProvided: boolean;
  detailsHousingAvailable: boolean;
  detailsCulturalSupport: boolean;
  detailsWheelchairAccessible: boolean;
  timesOfDay: string | null;
  expectedTime: string | null;
};

export function VolopDetailsList({
  startDate,
  startTime,
  endDate,
  endTime,
  startsLocal,
  endsLocal,
  feeAmount,
  feeRequired,
  isRecurring,
  timezone,
  welcomeGroups,
  welcomeFamilies,
  welcomeIntl,
  detailsStipendProvided,
  detailsTrainingProvided,
  detailsHousingAvailable,
  detailsCulturalSupport,
  detailsWheelchairAccessible,
  timesOfDay,
  expectedTime,
}: Props) {
  const formattedTimesOfDay =
    timesOfDay === 'NONE'
      ? null
      : [...TIMES_OF_DAY, ...TIMES_OF_DAY_VOLOPS].find(
          ({ id }) => id === timesOfDay,
        );

  const formattedExpectedTime =
    expectedTime === 'NONE'
      ? null
      : [
          ...EXPECTED_TIMES,
          ...EXPECTED_TIMES_ONCE,
          ...EXPECTED_TIMES_RECURRING,
        ].find(({ id }) => id === expectedTime);

  return (
    <DetailsList>
      {startDate &&
        endDate &&
        startsLocal &&
        endsLocal &&
        startDate === endDate && (
          <DetailsListItem
            title={getText('Date')}
            iconName="calendar"
            value={
              <SingleDayDateTime
                endsLocal={endsLocal}
                startsLocal={startsLocal}
                startTime={startTime}
                endTime={endTime}
                timezone={timezone}
              />
            }
          />
        )}

      {startDate && startsLocal && startDate !== endDate && (
        <DetailsListItem
          title={getText('Start Date')}
          iconName="hourglass"
          value={<DateTime datetime={startsLocal} timezone={timezone} />}
        />
      )}

      {endDate && endsLocal && endDate !== startDate && (
        <DetailsListItem
          title={getText('End Date')}
          iconName="hourglass"
          value={<DateTime datetime={endsLocal} timezone={timezone} />}
        />
      )}

      {formattedTimesOfDay && (
        <DetailsListItem
          title={getText('Schedule')}
          iconName="calendar"
          value={formattedTimesOfDay.name}
        />
      )}

      {formattedExpectedTime && (
        <DetailsListItem
          title={getText('Time Commitment')}
          iconName="clock"
          value={formattedExpectedTime.name}
        />
      )}

      <DetailsListItem
        title={getText('Recurrence')}
        iconName="hourglass"
        value={isRecurring ? getText('Recurring') : getText('One time only')}
      />

      {feeRequired && (
        <DetailsListItem
          title={getText('Cost')}
          iconName="coin"
          value={`${getText(
            'Fee Required',
          )}${feeAmount ? `: ${formatAmountString(feeAmount)}` : ''}`}
        />
      )}

      <DetailListSection title={getText('Benefits')}>
        {detailsStipendProvided && (
          <DetailsListItem
            iconName="coin"
            value={getText('Stipend Provided')}
          />
        )}

        {detailsTrainingProvided && (
          <DetailsListItem
            iconName="paper"
            value={getText('Training Provided')}
          />
        )}

        {detailsHousingAvailable && (
          <DetailsListItem
            iconName="house"
            value={getText('Housing Available')}
          />
        )}

        {detailsCulturalSupport && (
          <DetailsListItem
            iconName="speech-bubble"
            value={getText('Language / Cultural Support Available')}
          />
        )}
      </DetailListSection>

      <DetailListSection title={getText('Participation')}>
        {welcomeGroups && (
          <DetailsListItem iconName="group" value={getText('Groups Welcome')} />
        )}

        {welcomeFamilies && (
          <DetailsListItem
            iconName="family"
            value={getText('Families Welcome')}
          />
        )}

        {detailsWheelchairAccessible && (
          <DetailsListItem
            iconName="wheelchair"
            value={getText('Wheelchair Accessible')}
          />
        )}

        {welcomeIntl && (
          <DetailsListItem
            iconName="check-globe"
            value={getText('International Volunteers Welcome')}
          />
        )}
      </DetailListSection>
    </DetailsList>
  );
}
